import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
    PLAN_TYPE_OPTIONS,
    SCALE_DENOMINATOR_OPTIONS,
    SHEET_FORMAT_OPTIONS,
    drawingSheetInputSchema,
    formatScale,
    type DrawingSheetDocType,
    type FindingDocType,
    type FindingPlanumDocType,
    type FindingProfileDocType
} from '@ast-data/shared';
import {
    joinedListSortComparator,
    dateColumn,
    type GridColDef,
    naturalSortComparator
} from '../../lib/dataGrid';
import { useRecordActionsColumn } from '../../components/useGridRowActions';
import { useAuditColumns } from '../../components/auditColumns';
import { HIDDEN_AUDIT_COLUMNS } from '../../components/auditFields';
import {
    useInlineEditColumn,
    type InlineField
} from '../../components/gridInlineEdit';
import { joinedToChips } from '../../components/formFields';
import { withRecentValues } from '../../components/recentValues';
import { useIsoWeekColumn } from '../../components/isoWeekColumn';
import { usePersonPicker } from '../../components/personPicker';
import {
    resolveKeepingStored,
    storedPersonLinkList,
    usePersonUserId
} from '../users/personUserId';
import {
    useDetailColumnHeader,
    useFindingRefFilterColumns
} from './findingRefLabels';
import { useFindingRefsColumn } from './useFindingRefsColumn';

/**
 * The rest of the sheet record — in the column picker and the filter panel,
 * hidden by default. `drafterId` (the app-user link behind the Zeichner name)
 * and the reference id arrays stay out; the audit stamps come from
 * {@link useAuditColumns}, hidden the same way.
 */
const DRAWING_SHEET_DETAIL_FIELDS = [
    'area',
    'trench',
    'planum',
    'profile',
    'sheetFormat',
    'scale',
    'period',
    'remark'
] as const;

/** The offered Massstäbe, canonically spelled ("M 1:20"). */
const SCALE_OPTIONS = SCALE_DENOMINATOR_OPTIONS.map(formatScale);

/**
 * A list field's stored value: already a list on every row but the one just
 * edited — a row update writes ALL editable fields, and re-reading a stored
 * list as text would fold it into a single entry.
 */
function listValue(value: unknown, options: readonly string[]): string[] {
    return Array.isArray(value)
        ? (value as string[])
        : joinedToChips(value, options);
}

/**
 * The Zeichenblatt fields editable in the grid — one definition for the columns
 * and the save, like the Fotos (see useInlinePhotoFields).
 *
 * These four are what a stack of sheets has in common: they come off the same
 * plotter in the same format, at the scale the day was drawn at, showing the
 * same kind of plan, by whoever sat at the table. Only the Blattformat is plain
 * text; the other three say what they really are through `parse` — the cell
 * edits what a person reads, the record keeps what it stores:
 *
 * - the Massstab is stored canonically (`M 1:50`), so anything naming that
 *   number — "50", "1:50" — is accepted and normalized, exactly as `ScaleField`
 *   does in the form;
 * - Zeichner and Planart are LISTS shown ', '-joined, split back against their
 *   own vocabulary (a Planart may contain a comma — see joinedToChips), and the
 *   Zeichner carries `drafterId` positional beside it, which is why the account
 *   links are resolved from the parsed list.
 */
export function useInlineDrawingSheetFields(
    sheets: readonly DrawingSheetDocType[]
): InlineField[] {
    const { accounts } = usePersonPicker();
    const { resolve } = usePersonUserId();

    return useMemo(
        () =>
            // All four offer their last values on F2 — a stack of sheets comes
            // off one plotter, at one scale, drawn by the same hand.
            withRecentValues(
                [
                    {
                        field: 'sheetFormat',
                        editor: {
                            kind: 'suggest',
                            options: SHEET_FORMAT_OPTIONS
                        }
                    },
                    {
                        field: 'scale',
                        /**
                         * The same denominators the form offers, spelled as the record
                         * stores them — a cell shows `M 1:50`, so a list of bare
                         * numbers beside it would read as a different kind of value.
                         * Typing the number alone still works: that is what `parse` is.
                         */
                        editor: { kind: 'suggest', options: SCALE_OPTIONS },
                        parse: (value: unknown) =>
                            formatScale(String(value ?? ''))
                    },
                    {
                        field: 'planTypes',
                        editor: { kind: 'multi', options: PLAN_TYPE_OPTIONS },
                        parse: (value: unknown) =>
                            listValue(value, PLAN_TYPE_OPTIONS)
                    },
                    {
                        field: 'drafter',
                        editor: { kind: 'person', multiple: true },
                        parse: (value: unknown) => listValue(value, accounts),
                        /**
                         * The cell commits the WHOLE list, so the Zeichner
                         * who stayed keep the accounts they had — only a name
                         * new to the sheet is resolved.
                         */
                        derive: (
                            value: unknown,
                            stored?: Record<string, unknown>
                        ) => ({
                            drafterId: resolveKeepingStored(
                                value as string[],
                                storedPersonLinkList(
                                    (stored?.drafter as string[]) ?? [],
                                    stored?.drafterId as string[] | undefined
                                ),
                                resolve
                            )
                        })
                    }
                ],
                sheets
            ),
        [accounts, resolve, sheets]
    );
}

/** Detail columns shown anyway — the ones edited here (see the Funde grid). */
const VISIBLE_DRAWING_SHEET_DETAIL_FIELDS: readonly string[] = [
    'sheetFormat',
    'scale'
];

/** `columnVisibilityModel` entries hiding the detail columns by default. */
export const HIDDEN_DRAWING_SHEET_DETAIL_COLUMNS: Record<string, boolean> = {
    ...Object.fromEntries(
        DRAWING_SHEET_DETAIL_FIELDS.filter(
            (field) => !VISIBLE_DRAWING_SHEET_DETAIL_FIELDS.includes(field)
        ).map((field) => [field, false])
    ),
    ...HIDDEN_AUDIT_COLUMNS,
    recordedAt: false
};

/** Column definitions for the Zeichenblätter grid. */
export function useDrawingSheetsColumns({
    findings,
    planums,
    profiles,
    canEdit,
    inlineFields = [],
    onEdit,
    onDelete,
    onHistory
}: {
    findings: FindingDocType[];
    planums: FindingPlanumDocType[];
    profiles: FindingProfileDocType[];
    canEdit: boolean;
    /** Which columns are edited in the grid — see useInlineDrawingSheetFields. */
    inlineFields?: readonly InlineField[];
    onEdit: (row: DrawingSheetDocType) => void;
    onDelete: (row: DrawingSheetDocType) => void;
    onHistory: (row: DrawingSheetDocType) => void;
}): GridColDef<DrawingSheetDocType>[] {
    const intl = useIntl();
    const detailHeader = useDetailColumnHeader('drawingSheet');
    const actionsColumn = useRecordActionsColumn<DrawingSheetDocType>({
        canEdit,
        onEdit,
        onDelete,
        onHistory
    });

    const auditColumns = useAuditColumns<DrawingSheetDocType>();
    const inlineEdit = useInlineEditColumn<DrawingSheetDocType>(
        drawingSheetInputSchema,
        canEdit
    );
    const inlineOf = (field: string) =>
        inlineFields.find((spec) => spec.field === field);
    const isoWeekColumn = useIsoWeekColumn<DrawingSheetDocType>(
        (row) => row.recordedAt
    );

    const refsColumn = useFindingRefsColumn<DrawingSheetDocType>({
        headerId: 'drawingSheet.field.findings',
        findings,
        planums,
        profiles
    });
    // Hidden, filterable Befundnummer/Fläche/Planum/Profil columns for the filter panel.
    const findingFilterColumns =
        useFindingRefFilterColumns<DrawingSheetDocType>(
            findings,
            planums,
            profiles
        );

    return [
        {
            field: 'drawingSheetNumber',
            headerName: intl.formatMessage({
                id: 'drawingSheet.field.drawingSheetNumber'
            }),
            width: 190,
            // Numeric-aware so "Bl 2" sorts before "Bl 10".
            sortComparator: naturalSortComparator,
            // Bold like the other entities' key column — it is the row's key.
            cellClassName: 'grid-cell-bold'
        },
        // Straight after the pinned key column: when a record was taken is the
        // first thing anyone reads off a row (same order in every grid).
        isoWeekColumn,
        dateColumn<DrawingSheetDocType>({
            field: 'recordedAt',
            headerName: intl.formatMessage({ id: 'common.recordedAt' }),
            width: 120
        }),
        {
            field: 'fileName',
            headerName: intl.formatMessage({
                id: 'drawingSheet.field.fileName'
            }),
            flex: 1,
            minWidth: 160
        },
        {
            field: 'planTypes',
            headerName: intl.formatMessage({
                id: 'drawingSheet.field.planTypes'
            }),
            width: 180,
            sortComparator: joinedListSortComparator,
            /**
             * A list on the record, one joined string in the cell — and briefly
             * that string on the row itself, between the edit being committed
             * and the patched row coming back.
             */
            valueGetter: (_value, row) =>
                Array.isArray(row.planTypes)
                    ? row.planTypes.join(', ')
                    : String(row.planTypes ?? ''),
            ...(inlineOf('planTypes')
                ? inlineEdit('planTypes', inlineOf('planTypes'))
                : {})
        },
        { ...refsColumn, width: 240 },
        {
            field: 'drafter',
            headerName: intl.formatMessage({
                id: 'drawingSheet.field.drafter'
            }),
            width: 150,
            /**
             * A list on the record, one joined string in the cell — and briefly
             * that string on the row itself, between the edit being committed
             * and the patched row coming back.
             */
            valueGetter: (_value, row) =>
                Array.isArray(row.drafter)
                    ? row.drafter.join(', ')
                    : String(row.drafter ?? ''),
            ...(inlineOf('drafter')
                ? inlineEdit('drafter', inlineOf('drafter'))
                : {})
        },
        ...findingFilterColumns,
        ...DRAWING_SHEET_DETAIL_FIELDS.map<GridColDef<DrawingSheetDocType>>(
            (field) => ({
                field,
                headerName: detailHeader(field),
                width: 140,
                ...(inlineOf(field) ? inlineEdit(field, inlineOf(field)) : {})
            })
        ),
        ...auditColumns,
        actionsColumn
    ];
}
